import React from 'react';
import styled from '@emotion/styled';
import { RecoilRoot } from 'recoil';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';

import { ThemeProvider, GlobalStyles } from '@Calix-Commerce/design-system/globals';
import { Container, Flex } from '@Calix-Commerce/design-system/layout';

import { Orders, Order, PurchasedOrder } from 'pages';
import { client } from 'connectors/apollo';
import { initializeState } from 'state/initializeState';
import {
  Header,
  Footer,
  Breadcrumb,
  LoaderOverlay,
  LocalStorageSync,
  ErrorBoundary,
  GoogleAnalytics,
  AuthOverlay,
  Notification,
  GoogleTagManager,
  PriceChangeNotification,
} from 'components';

const AppStyled = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.div`
  flex-grow: 1;
`;

// Log out the environment variables when not in production
if (process.env.REACT_APP_ENV !== 'prod') {
  console.log('Environment Variables Loaded: ', {
    REACT_APP_WALKME_URL: process.env.REACT_APP_WALKME_URL,
    REACT_APP_GOOGLE_ANALYTICS_ID: process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
    REACT_APP_GRAPHQL_API_KEY: process.env.REACT_APP_GRAPHQL_API_KEY,
    PUBLIC_URL: process.env.PUBLIC_URL,
    REACT_APP_BUGSNAG_API_KEY: process.env.REACT_APP_BUGSNAG_API_KEY,
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    REACT_APP_VERSION: process.env.REACT_APP_VERSION,
  });
}

const MaintenancePage = () => {
  return (
    <Flex alignment="baseline" spacing="space-around">
      <div>
        <h1>Maintenance</h1>
        <p>The My Calix Store is currently undergoing maintenance and will be back up shortly.</p>
      </div>
    </Flex>
  );
};

const App = () => {
  const loadGA = process.env.REACT_APP_GOOGLE_ANALYTICS_ID !== 'false';
  const inMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  return (
    <ApolloProvider client={client}>
      <RecoilRoot initializeState={initializeState}>
        <GlobalStyles />
        {inMaintenanceMode ? (
          <MaintenancePage />
        ) : (
          <ErrorBoundary>
            <ThemeProvider>
              <LoaderOverlay />
              <Notification />
              <LocalStorageSync />
              <AppStyled>
                <Router basename="/order-manager">
                  {loadGA ? <GoogleAnalytics /> : null}
                  <AuthOverlay>
                    <GoogleTagManager />
                    <Header />
                    <Container>
                      <PriceChangeNotification />
                    </Container>
                    <Breadcrumb />
                    <MainContent>
                      <Switch>
                        <Route exact path="/orders" component={Orders} />
                        <Route exact path="/orders/:orderNumber" component={Order} />
                        <Route exact path="/purchase-order/:poNumber" component={PurchasedOrder} />
                        <Redirect to="/orders" />
                      </Switch>
                    </MainContent>
                    <Footer />
                  </AuthOverlay>
                </Router>
              </AppStyled>
            </ThemeProvider>
          </ErrorBoundary>
        )}
      </RecoilRoot>
    </ApolloProvider>
  );
};

export default App;
